import { useEffect, useMemo } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSchool,
  faLanguage,
  faIdCard,
  faPhone,
  faToggleOn,
  faToggleOff,
  faPlusCircle,
  faCircleCheck
} from "@fortawesome/free-solid-svg-icons";
import UnifiedDatePicker from "../../components/UnifiedDatePicker";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AddSchool, saveLogin, fetchData } from "../../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import Can from "../../permissions/Can";

export default function AddSchoolPage() {
  const mdeeriatData = useSelector((state) => state.main.data?.mdeeriatData);
  const isAuth = useSelector((state) => state.main.isAuthenticated);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      isOn: true
    }
  });

  const mdeeriatOptions = useMemo(() =>
    mdeeriatData?.map(m => ({ value: m.id, label: m.mod_name_ar })) || []
    , [mdeeriatData]);

  const handleAddSchool = handleSubmit((data) => {
    if (!data.nationalNumber || isNaN(Number(data.nationalNumber))) {
      toast.error("يرجى إدخال الرقم الوطني للمدرسة");
      return;
    }

    const validDate = data.date || null;

    const schoolData = {
      rgmw: Number(data.nationalNumber),
      schoolname: data.arabicName,
      eng_schoolname: data.englishName,
      phonen: data.phoneNumber ? Number(data.phoneNumber) : 0,
      paydate: validDate,
      onoff: data.isOn ? "on" : "off",
      modname: Number(data.directorate),
    };

    toast((t) => (
      <div style={{ fontFamily: "Cairo, sans-serif", textAlign: "center" }}>
        <div>هل تريد إضافة المدرسة:</div>
        <div style={{ color: "var(--accent)", fontWeight: 700, margin: "5px 0" }}>
          {schoolData.schoolname}
        </div>
        <div style={{ marginTop: 15, display: "flex", justifyContent: "center", gap: "10px" }}>
          <button
            onClick={async () => {
              try {
                await dispatch(AddSchool(schoolData)).unwrap();
                toast.success("تم إضافة المدرسة بنجاح");
                reset();
                setValue("directorate", null);
                dispatch(fetchData(true));
              } catch {
                toast.error("فشل في إضافة المدرسة");
              } finally {
                toast.dismiss(t.id);
              }
            }}
            style={{
              backgroundColor: "var(--accent)",
              color: "white",
              padding: "8px 18px",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontFamily: "Cairo, sans-serif",
              fontWeight: "600"
            }}
          >
            نعم، متأكد
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{
              backgroundColor: "var(--panel-2)",
              color: "var(--text)",
              padding: "8px 18px",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              cursor: "pointer",
              fontFamily: "Cairo, sans-serif",
            }}
          >
            تراجع
          </button>
        </div>
      </div>
    ), { duration: 8000, style: { background: "var(--panel)", color: "var(--text)", border: "1px solid var(--border)" } });
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    if (!mdeeriatData || mdeeriatData.length === 0) {
      dispatch(fetchData());
    }
  }, [dispatch, mdeeriatData]);

  useEffect(() => {
    if (!isAuth) {
      const storedUserCredentials = JSON.parse(localStorage.getItem("userCredentials"));
      if (storedUserCredentials) {
        dispatch(saveLogin(storedUserCredentials));
      } else {
        navigate("/");
      }
    }
  }, [isAuth, dispatch, navigate]);

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      height: "44px",
      minHeight: "44px",
      background: "var(--panel-2)",
      borderColor: state.isFocused ? "var(--accent)" : "var(--border)",
      borderRadius: "12px",
      color: "var(--text)",
      fontFamily: "Cairo",
      boxShadow: state.isFocused ? "0 0 0 1px var(--accent)" : "none",
      display: "flex",
      alignItems: "center",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        borderColor: state.isFocused ? "var(--accent)" : "rgba(255,255,255,0.3)"
      }
    }),
    valueContainer: (base) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      height: "44px",
      padding: "0 12px"
    }),
    indicatorSeparator: () => ({
      display: "none"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    menu: (base) => ({
      ...base,
      background: "#1e293b",
      border: "1px solid var(--border)",
      borderRadius: "12px",
      overflow: "hidden",
    }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected ? "var(--accent)" : state.isFocused ? "rgba(255,255,255,0.05)" : "transparent",
      color: "#fff",
      cursor: "pointer",
      fontSize: "14px",
      fontFamily: "Cairo",
      textAlign: "right",
      "&:active": {
        background: "var(--accent)"
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text)",
      fontFamily: "Cairo",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--muted)",
      fontSize: "14px",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    input: (base) => ({
      ...base,
      color: "var(--text)",
      textAlign: "right",
      margin: 0,
      padding: 0
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--muted)",
      padding: "0 12px",
      "&:hover": {
        color: "var(--accent)"
      }
    })
  };

  return (
    <div className="school-add-wrapper anim-fade-in" style={{ padding: "5px" }}>
      <div className="premium-edit-container">
        {/* Premium Header Modern */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <FontAwesomeIcon icon={faPlusCircle} />
            </div>
            <div className="header-text-box">
              <h2>إضافة مدرسة جديدة</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">
                  إعداد مدرسة
                </span>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleAddSchool} className="premium-form">
          <div className="section-grid">
            {/* Field 1 */}
            <div className="input-field span-2">
              <label>اسم المدرسة (بالعربية)</label>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faSchool} className="field-icon" />
                <input type="text" {...register("arabicName")} placeholder="أدخل اسم المدرسة بالعربية" required />
              </div>
            </div>

            {/* Field 2 */}
            <div className="input-field">
              <label>الرقم الوطني</label>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faIdCard} className="field-icon" />
                <input type="text" {...register("nationalNumber")} placeholder="2711XXXX" required />
              </div>
            </div>

            {/* Field 3 */}
            <div className="input-field">
              <label>اسم المدرسة (بالإنجليزي)</label>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faLanguage} className="field-icon" />
                <input type="text" {...register("englishName")} placeholder="School Name" />
              </div>
            </div>

            {/* Field 4 */}
            <div className="input-field">
              <label>رقم الهاتف</label>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faPhone} className="field-icon" />
                <input type="text" {...register("phoneNumber")} placeholder="0569501XXX" />
              </div>
            </div>

            {/* Field 5 */}
            <div className="input-field">
              <label>تاريخ تفعيل الاشتراك</label>
              <UnifiedDatePicker
                value={watch("date")}
                onChange={(date) => setValue("date", date)}
                portalId="root"
              />
            </div>

            {/* Field 6 - Select Full Width in middle */}
            <div className="input-field span-3">
              <label>المديرية التابعة لها</label>
              <Select
                options={mdeeriatOptions}
                placeholder="ابحث عن المديرية..."
                styles={customSelectStyles}
                onChange={(option) => setValue("directorate", option?.value)}
                isRtl={true}
                isLoading={!mdeeriatData}
                isSearchable={true}
                menuPortalTarget={document.body}
              />
            </div>

            {/* Field 7 - Compact Toggle */}
            <div className="input-field span-3" style={{ marginTop: "5px" }}>
              <div
                onClick={() => setValue("isOn", !watch("isOn"))}
                className="premium-toggle-card"
                style={{ padding: "12px 20px", borderRadius: "14px" }}
              >
                <div className="toggle-info" style={{ gap: "15px" }}>
                  <FontAwesomeIcon
                    icon={watch("isOn") ? faToggleOn : faToggleOff}
                    className={`toggle-icon ${watch("isOn") ? "active" : ""}`}
                    style={{ fontSize: "28px" }}
                  />
                  <div className="toggle-text">
                    <span className="main-label" style={{ fontSize: "14px" }}>تفعيل نظام الفارس فوراً</span>
                    <span className="sub-label" style={{ fontSize: "11px" }}>منح الصلاحية فور الحفظ</span>
                  </div>
                </div>
                {watch("isOn") && <FontAwesomeIcon icon={faCircleCheck} className="check-icon" />}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Can module="schools" action="add">
            <button type="submit" className="premium-submit-btn">
              <div className="btn-content">
                <FontAwesomeIcon icon={faPlusCircle} />
                <span>حفظ وإضافة المدرسة للنظام</span>
              </div>
            </button>
            </Can>
          </div>
        </form>
      </div>

      <style>{`
        .school-add-wrapper { 
          padding: 5px; 
          color: var(--text); 
          font-family: 'Cairo', sans-serif; 
        }
        
        .premium-edit-container {
          margin: 0;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
        }

        .premium-header-modern {
          display: flex; justify-content: space-between; align-items: center;
          padding: 15px 40px; 
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px 24px 0 0;
          gap: 20px;
        }

        .premium-form {
          background: rgba(255, 255, 255, 0.5);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 24px 24px;
          padding: 30px 40px;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .header-icon-box {
          width: 45px;
          height: 45px;
          background: var(--accent);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: white;
          box-shadow: 0 8px 16px -4px rgba(14, 165, 233, 0.4);
        }

        .header-text-box h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
        }

        .breadcrumb-info {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--muted);
          margin-top: 4px;
        }

        .breadcrumb-info .separator {
          opacity: 0.5;
        }

        .breadcrumb-info .current-page {
          color: var(--accent);
          font-weight: 600;
        }
        
        .section-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .span-2 { grid-column: span 2; }
        .span-3 { grid-column: span 3; }
        .input-field label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 8px; color: var(--muted); margin-right: 5px; }
        .input-wrapper { position: relative; display: flex; align-items: center; }
        .field-icon { position: absolute; right: 15px; color: var(--muted); font-size: 14px; opacity: 0.5; }
        .input-wrapper input {
          width: 100%; height: 44px; background: var(--panel-2); border: 1px solid var(--border);
          border-radius: 12px; padding: 0 40px 0 15px; color: var(--text); font-family: inherit; font-size: 14px; transition: all 0.2s;
        }
        .input-wrapper input:focus { border-color: var(--accent); outline: none; box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 10%, transparent); }
        
        .premium-toggle-card {
           display: flex; align-items: center; justify-content: space-between;
           background: var(--panel-2); border: 1px solid var(--border); cursor: pointer; transition: 0.3s;
        }
        .premium-toggle-card:hover { border-color: var(--accent); background: color-mix(in srgb, var(--panel-2) 95%, var(--accent)); }
        .toggle-info { display: flex; align-items: center; }
        .toggle-icon { color: var(--muted); transition: 0.3s; }
        .toggle-icon.active { color: var(--success); }
        .toggle-text { display: flex; flex-direction: column; }
        .main-label { font-weight: 700; color: var(--text); }
        .sub-label { color: var(--muted); }
        .check-icon { font-size: 18px; color: var(--success); }

        .form-actions {
          margin-top: 30px;
          display: flex;
          justify-content: flex-end;
        }

        .premium-submit-btn {
          background: linear-gradient(135deg, #28917f 0%, #1d7567 100%);
          border: none; color: white; font-size: 15px; font-weight: 600; cursor: pointer;
          position: relative; overflow: hidden; transition: 0.3s;
          box-shadow: 0 10px 20px -8px rgba(29, 117, 103, 0.4); font-family: 'Cairo', sans-serif;
          padding: 12px 24px;
          border-radius: 6px;
          min-width: 200px;
        }
        .btn-content { position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; gap: 10px; }
        .premium-submit-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }

        @media (max-width: 768px) {
          .premium-header-modern {
            padding: 15px 20px;
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }
          .premium-form {
            padding: 20px;
          }
          .section-grid { grid-template-columns: 1fr !important; }
          .span-2, .span-3 { grid-column: span 1; }
          .premium-submit-btn { width: 100%; }
        }
      `}</style>
    </div>
  );
}
